import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';
import { getFeedbackDetail, listFeedback, postFeedback } from '../lib/api';
import { toast } from '../lib/toast';
import type { FeedbackLogRow } from '../lib/types';

interface Props {
  onClose: () => void;
}

/** Two-pane modal: top half is a "file feedback" form (issue / tool suggestion), bottom half
 *  is the read-only feedback log. The result-row buttons handle thumbs-up/relevant directly,
 *  so the form here is just for free-text issues and tool suggestions. */
export function FeedbackModal({ onClose }: Props) {
  const [tab, setTab] = useState<'file' | 'log'>('log');
  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-zinc-900 rounded-lg shadow-xl p-4 max-w-3xl w-full max-h-[85vh] flex flex-col border border-zinc-200 dark:border-zinc-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-lg font-semibold flex-1">Feedback</h2>
          <div className="flex rounded border border-zinc-300 dark:border-zinc-700 overflow-hidden text-xs">
            <button
              onClick={() => setTab('log')}
              className={clsx('px-3 py-1', tab === 'log' && 'bg-indigo-600 text-white')}
            >
              Recent
            </button>
            <button
              onClick={() => setTab('file')}
              className={clsx('px-3 py-1', tab === 'file' && 'bg-indigo-600 text-white')}
            >
              File new
            </button>
          </div>
          <button
            onClick={onClose}
            className="text-sm px-2 py-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
          >
            Close
          </button>
        </div>
        {tab === 'log' ? <FeedbackLog /> : <FeedbackForm onSubmitted={() => setTab('log')} />}
      </div>
    </div>
  );
}

function FeedbackLog() {
  const q = useQuery({
    queryKey: ['feedback-log'],
    queryFn: () => listFeedback({ limit: 50 }),
  });
  const rows = q.data?.feedback ?? [];
  return (
    <div className="overflow-y-auto flex-1 -mx-4 px-4">
      {q.isLoading && <div className="text-sm text-zinc-500">Loading…</div>}
      {q.error && <div className="text-sm text-rose-600">Failed to load feedback.</div>}
      {!q.isLoading && rows.length === 0 && (
        <div className="text-sm text-zinc-500">No feedback yet.</div>
      )}
      <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
        {rows.map((r) => (
          <FeedbackRow key={r.id} row={r} />
        ))}
      </ul>
    </div>
  );
}

const KIND_BADGE: Record<string, string> = {
  issue: 'bg-rose-100 dark:bg-rose-900 text-rose-900 dark:text-rose-100',
  result_rating: 'bg-sky-100 dark:bg-sky-900 text-sky-900 dark:text-sky-100',
  relevant: 'bg-amber-100 dark:bg-amber-900 text-amber-900 dark:text-amber-100',
  tool_suggestion: 'bg-violet-100 dark:bg-violet-900 text-violet-900 dark:text-violet-100',
};

/** One feedback row. Click the header to expand the linked tool_call — the tool, its
 *  correlation id (grep the server logs by it), and the result set the agent actually saw. */
function FeedbackRow({ row }: { row: FeedbackLogRow }) {
  const [open, setOpen] = useState(false);
  const detail = useQuery({
    queryKey: ['feedback-detail', row.id],
    queryFn: () => getFeedbackDetail(row.id),
    enabled: open,
  });
  const call = detail.data?.tool_call ?? null;
  return (
    <li className="py-2 text-sm">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-baseline gap-2 mb-1 text-left"
      >
        <span className="font-mono text-xs text-zinc-400">{open ? '▾' : '▸'}</span>
        <span className="font-mono text-xs text-zinc-500">#{row.id}</span>
        <span
          className={clsx(
            'text-[10px] uppercase font-medium tracking-wide px-1 py-0.5 rounded',
            KIND_BADGE[row.kind],
          )}
        >
          {row.kind}
        </span>
        <span className="text-xs text-zinc-500 font-mono">{row.ts}</span>
        <span className="ml-auto text-[10px] uppercase font-medium tracking-wide text-zinc-500">
          {row.status}
        </span>
      </button>
      <pre className="text-xs font-mono bg-zinc-50 dark:bg-zinc-950 p-2 rounded overflow-x-auto">
        {JSON.stringify(row.payload, null, 2)}
      </pre>
      {open && (
        <div className="mt-1 pl-3 border-l-2 border-zinc-200 dark:border-zinc-700">
          {detail.isLoading && <div className="text-xs text-zinc-500">Loading the call…</div>}
          {detail.error && (
            <div className="text-xs text-rose-600">Failed to load the linked call.</div>
          )}
          {detail.data && !call && (
            <div className="text-xs text-zinc-500">No tool call linked to this feedback.</div>
          )}
          {call && (
            <div className="text-xs space-y-1 py-1">
              <div className="text-zinc-600 dark:text-zinc-400">
                <span className="font-medium">{call.tool_name}</span>
                {` · ${call.num_results ?? '?'} results`}
                {call.top_score != null && ` · top ${call.top_score.toFixed(3)}`}
                {call.latency_ms != null && ` · ${call.latency_ms}ms`}
              </div>
              {call.call_id && (
                <div className="font-mono text-[11px] text-zinc-500">
                  call_id {call.call_id} — grep the server logs by this
                </div>
              )}
              {call.results && call.results.length > 0 ? (
                <ol className="space-y-0.5">
                  {call.results.map((res, i) => (
                    <li
                      key={i}
                      className="font-mono text-[11px] text-zinc-600 dark:text-zinc-400"
                    >
                      {res.score != null && `[${res.score.toFixed(3)}] `}
                      {res.repo}/{res.file}:{res.start_line}-{res.end_line}
                      {res.qual_name && ` ${res.qual_name}`}
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="text-zinc-500">No result snapshot recorded for this call.</div>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function FeedbackForm({ onSubmitted }: { onSubmitted: () => void }) {
  const [kind, setKind] = useState<'issue' | 'tool_suggestion'>('issue');
  const [text, setText] = useState('');
  const [severity, setSeverity] = useState<'low' | 'medium' | 'high'>('medium');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload =
        kind === 'issue'
          ? { description: text, severity }
          : { name: text.split('\n')[0]?.trim() || 'unnamed', description: text };
      await postFeedback(kind, payload);
      toast('feedback recorded', 'success');
      setText('');
      onSubmitted();
    } catch (e: unknown) {
      toast(`failed: ${(e as Error).message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3 flex-1">
      <div className="flex gap-2 items-center text-sm">
        <label className="font-medium">Kind:</label>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value as 'issue' | 'tool_suggestion')}
          className="rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2 py-1 text-sm"
        >
          <option value="issue">issue</option>
          <option value="tool_suggestion">tool_suggestion</option>
        </select>
        {kind === 'issue' && (
          <>
            <label className="font-medium ml-3">Severity:</label>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as 'low' | 'medium' | 'high')}
              className="rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2 py-1 text-sm"
            >
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
            </select>
          </>
        )}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        required
        placeholder={
          kind === 'issue'
            ? 'What did the tool do wrong? What did you expect?'
            : 'Proposed tool name on the first line; description below.'
        }
        className="flex-1 min-h-[120px] rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 px-2 py-1.5 text-sm font-mono"
      />
      <button
        type="submit"
        disabled={submitting || !text.trim()}
        className="self-end rounded bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white text-sm font-medium px-3 py-2"
      >
        {submitting ? 'Sending…' : 'Submit'}
      </button>
    </form>
  );
}
